import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Anchor, Ban, Check, Clock, Clover, Coins, Crown, Flag, Flame, HandCoins, Landmark, Lock, Megaphone, Pin, PinOff, ReceiptText, Scroll, Swords, Vote, Waves, X } from "lucide-react";
import { api } from "@/lib/api";
import { Face, utcTime } from "@/city3d/Hud";
import { Bar, Btn, Info, Ring, Section, Skel, Uxd } from "./kit";
import { ACC, dur, fmt, useNow } from "./util";

const DAY = 24 * 36e5, TERM = 2 * DAY;
const MAIN = "!bg-x-gold !text-white hover:!opacity-90 disabled:!bg-x-raised disabled:!text-x-mute";
const FIELD = "hud-well min-w-0 rounded-xl px-3 py-2 text-sm font-semibold outline-none placeholder:text-x-mute focus:ring-2 focus:ring-x-gold";
const POWERS = [[Coins, "2% tax"], [Landmark, "Sign"], [Pin, "Pin"], [Scroll, "Decrees"], [Megaphone, "Address"], [HandCoins, "Grants"]];
const DECREES = { docks_day: ["Docks Day", "+50%", Anchor, "Ships pay +50% for a day"], fast_tide: ["Fast Tide", "2x", Waves, "Ships dock twice as fast for a day"],
  fireworks: ["Fireworks", "Show", Flame, "A show over the city"], tax_holiday: ["Tax Holiday", "No upkeep", ReceiptText, "No dock upkeep for a day"],
  lucky_cargo: ["Lucky Cargo", "2x luck", Clover, "Lucky cargo comes in twice as often for a day"], duel_night: ["Duel Night", "No fee", Swords, "Duel winners take the whole pot for a day"] };
const rules = (fee) => `The mayor gets a 2% harbour tax on every ship payout in the city (paid by the city) and 10% of what's spent in the city, their face and a MAYOR sign on City Hall, one pinned chat message, a new decree whenever the last one ends, an address to the whole city once a day, and grants from the city treasury: up to 10% of it each term. Running costs ${fmt(fee)} UXD. Everyone gets one free secret ballot; votes are counted every 2 days at 9:00 UTC.`;

const Timer = ({ iso, total }) => {
  const ms = new Date(iso) - useNow();
  return (
    <span className="flex items-center gap-1.5">
      <Ring value={ms / total} size={26} stroke={3} acc="gold"><Clock className="h-3 w-3 text-x-gold" /></Ring>
      <b className="text-sm font-semibold tabular-nums text-x-ink">{dur(ms)}</b>
    </span>
  );
};
const Name = ({ p, onVisit, className = "" }) => <button type="button" onClick={() => onVisit(p)} className={`truncate text-sm font-semibold hover:underline ${className}`}>@{p.h}</button>;
const List = ({ children }) => <ul className="space-y-1.5"><AnimatePresence>{children}</AnimatePresence></ul>;
const Row = ({ i = 0, on, ...p }) => (
  <motion.li layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0, transition: { delay: Math.min(i, 6) * 0.04, duration: 0.25 } }} exit={{ opacity: 0, transition: { duration: 0.15 } }}
    className={`rounded-xl p-2 transition-colors ${on ? "bg-x-gold-soft" : "bg-x-raised hover:bg-x-active"}`} {...p} />
);

function Stage({ e, onVisit }) {
  const [label, , Icon] = DECREES[e.decree?.kind] || [];
  return (
    <div className="rounded-2xl bg-x-gold-soft px-4 py-4" data-testid="hall-mayor">
      {e.mayor ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="flex flex-col items-center gap-1 text-center">
          <Crown className="h-6 w-6 text-x-gold" />
          <Face p={e.mayor} size={64} ring={ACC.gold} />
          <button type="button" onClick={() => onVisit(e.mayor)} className="mt-1 text-lg font-semibold hover:underline">@{e.mayor.h}</button>
          {e.mayor.slogan && <p className="line-clamp-2 break-words text-sm italic text-x-mute">“{e.mayor.slogan}”</p>}
          {Icon && (
            <span className="mt-1 flex items-center gap-2 rounded-full bg-x-surface py-0.5 pl-2.5 pr-2 text-xs font-semibold text-x-gold-ink">
              <Icon className="h-3.5 w-3.5 text-x-gold" />{label}<Timer iso={e.decree.until} total={DAY} />
            </span>
          )}
        </motion.div>
      ) : (
        <div className="flex flex-col items-center gap-1.5 text-center" data-testid="hall-no-mayor">
          <Crown className="h-10 w-10 text-x-gold opacity-50" />
          <b className="text-base">No mayor yet</b>
          <span className="flex items-center gap-1 text-xs font-medium text-x-mute"><Clock className="h-3.5 w-3.5 text-x-gold" />{utcTime(e.closes_at)}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
        {POWERS.map(([Power, label]) => <span key={label} className="flex items-center gap-1 rounded-full bg-x-surface px-2 py-0.5 text-[11px] font-medium text-x-gold-ink"><Power className="h-3 w-3 text-x-gold" />{label}</span>)}
      </div>
      <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-x-mute" data-testid="hall-treasury" title="What the city has collected from spending. The mayor can grant up to 10% of it each term.">
        <Landmark className="h-3.5 w-3.5 text-x-gold" />Treasury<b className="tabular-nums text-x-ink">{fmt(e.treasury)} UXD</b>
      </div>
    </div>
  );
}

function Race({ e, me, busy, onVote, onVisit }) {
  return (
    <Section id="race" icon={Vote} title="Race" info="One free ballot each race. Ballots stay secret until the count." right={<span data-testid="hall-countdown"><Timer iso={e.closes_at} total={TERM} /></span>}>
      {e.race.length ? (
        <List>
          {e.race.map((c, i) => {
            const on = c.k === e.ballot;
            return (
              <Row key={c.k} i={i} on={on}>
                <div className="flex items-center gap-2.5">
                  <Face p={c} size={40} ring={on ? ACC.gold : ACC.grey} />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <Name p={c} onVisit={onVisit} className="block max-w-full" />
                    {c.slogan && <p className="line-clamp-2 break-words text-[11px] italic text-x-mute">{c.slogan}</p>}
                  </div>
                  {c.k !== me?.k && (
                    <Btn tone={on ? "soft" : "gold"} className="flex w-16 items-center justify-center gap-1 !px-0 py-2" data-testid="ballot-button" aria-label="Vote" disabled={busy || on} onClick={() => onVote(c.h)}>
                      {on ? <Check className="h-4 w-4 text-x-gold" strokeWidth={3} /> : <><Vote className="h-4 w-4" />Vote</>}
                    </Btn>
                  )}
                </div>
              </Row>
            );
          })}
        </List>
      ) : <div className="flex items-center justify-center gap-2 py-2 text-x-mute"><Vote className="h-5 w-5" />0</div>}
    </Section>
  );
}

function Run({ e, uxd, busy, onRun }) {
  const [slogan, setSlogan] = useState(""), [sure, setSure] = useState(false), short = e.fee - uxd;
  return (
    <Section id="run" icon={Flag} title="Run" info={`Running costs ${fmt(e.fee)} UXD and isn't refunded.`}>
      {e.running ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
          className="flex items-center justify-center gap-2 rounded-xl bg-x-gold-soft py-2.5 font-semibold text-x-gold-ink"><Check className="h-5 w-5" strokeWidth={3} />On the ballot</motion.div>
      ) : (
        <div className="space-y-2.5">
          <div className="hud-well flex items-center gap-2 rounded-xl p-1.5 pl-3 focus-within:ring-2 focus-within:ring-x-gold">
            <input value={slogan} onChange={(x) => setSlogan(x.target.value)} maxLength={80} placeholder="Slogan" data-testid="run-slogan"
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-x-mute" />
            <Ring value={slogan.length / 80} size={28} stroke={3} acc={slogan.length > 70 ? "red" : "gold"}><span className="text-[11px] font-semibold tabular-nums">{80 - slogan.length}</span></Ring>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium tabular-nums text-x-mute">
            <Coins className="h-4 w-4 shrink-0 text-x-gold" /><div className="flex-1"><Bar value={uxd / e.fee} color={ACC.gold} /></div>{fmt(uxd)}/{fmt(e.fee)}
          </div>
          {sure ? (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs" title="This costs UXD and isn't refunded"><Ban className="h-4 w-4 text-x-mute" /><Uxd n={e.fee} /></span>
              <Btn className={`flex flex-1 items-center justify-center gap-1.5 py-2 ${MAIN}`} data-testid="run-mayor-confirm" disabled={busy} onClick={() => { setSure(false); onRun(slogan.trim()); }}>
                <Crown className="h-4 w-4" />Pay
              </Btn>
              <Btn tone="soft" className="px-3" aria-label="Cancel" onClick={() => setSure(false)}><X className="h-4 w-4" /></Btn>
            </div>
          ) : (
            <Btn className={`flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} data-testid="run-mayor" disabled={busy || short > 0 || !slogan.trim()} onClick={() => setSure(true)}>
              {short > 0 ? <><Coins className="h-4 w-4" />Need {fmt(short)}</> : <><Crown className="h-4 w-4" />Run · {fmt(e.fee)}</>}
            </Btn>
          )}
        </div>
      )}
    </Section>
  );
}

function Decree({ e, busy, onDecree }) {
  return (
    <Section id="decree" icon={Scroll} title="Decree" info="One decree at a time, each for a day. Pass the next one as soon as it ends."
      right={e.decree && <Timer iso={e.decree.until} total={DAY} />}>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(DECREES).map(([kind, [label, badge, Icon, desc]], i) => {
          const live = e.decree?.kind === kind, off = busy || !!e.decree;
          return (
            <motion.button key={kind} type="button" data-testid="decree-button" title={desc} aria-label={`${label}: ${desc}`} disabled={off} onClick={() => onDecree(kind)}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.25 } }} whileTap={off ? undefined : { scale: 0.96 }}
              className={`flex flex-col items-center gap-1 rounded-xl p-2.5 text-xs font-semibold transition-colors ${live ? "bg-x-gold-soft ring-2 ring-x-gold"
                : "bg-x-raised enabled:hover:bg-x-gold-soft disabled:opacity-50"}`}>
              <Icon className="h-6 w-6 text-x-gold" />
              <span>{label}</span>
              <span className="rounded-full bg-x-surface px-1.5 text-[11px] font-medium text-x-mute">{badge}</span>
            </motion.button>
          );
        })}
      </div>
    </Section>
  );
}

function Address({ e, busy, onSend }) {
  const [text, setText] = useState("");
  return (
    <Section id="address" icon={Megaphone} title="Address" info="Once a day: a card for everyone in town, and anyone who comes in over the next day."
      right={e.address_used && <Lock className="h-4 w-4" />}>
      {e.address && <p className="mb-2 whitespace-pre-line break-words rounded-xl bg-x-gold-soft p-2.5 text-xs" data-testid="hall-address">{e.address.text}</p>}
      {!e.address_used && (
        <div className="space-y-2">
          <textarea value={text} onChange={(x) => setText(x.target.value)} maxLength={200} rows={2} placeholder="Say something to the whole city" className={`${FIELD} w-full resize-none`} data-testid="address-text" />
          <Btn className={`flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} data-testid="address-send" disabled={busy || text.trim().length < 2}
            onClick={async () => (await onSend(text.trim())) && setText("")}>
            <Megaphone className="h-4 w-4" />Address the city
          </Btn>
        </div>
      )}
    </Section>
  );
}

function Grant({ e, busy, onGrant }) {
  const [h, setH] = useState(""), [amount, setAmount] = useState(""), n = Number(amount);
  return (
    <Section id="grant" icon={HandCoins} title="Grants" info="Give any resident UXD from the city treasury, up to 10% of it each term. What the city spends fills it."
      right={<span className="text-xs tabular-nums text-x-mute" data-testid="grant-room">{fmt(e.grant_room)} left</span>}>
      <div className="space-y-2">
        <div className="flex gap-2">
          <input value={h} onChange={(x) => setH(x.target.value.replace(/^@/, ""))} maxLength={20} placeholder="@handle" className={`${FIELD} flex-1`} data-testid="grant-handle" />
          <input value={amount} onChange={(x) => setAmount(x.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="UXD" className={`${FIELD} w-28 tabular-nums`} data-testid="grant-amount" />
        </div>
        <Btn className={`flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} data-testid="grant-send" disabled={busy || !h.trim() || !n || n > e.grant_room}
          onClick={async () => { if (await onGrant(h.trim(), n)) { setH(""); setAmount(""); } }}>
          <HandCoins className="h-4 w-4" />{n > e.grant_room ? `Up to ${fmt(e.grant_room)}` : `Grant${n ? ` · ${fmt(n)}` : ""}`}
        </Btn>
      </div>
    </Section>
  );
}

function PinCard({ e, busy, onPin }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    const load = () => api("/chat").then((c) => setMsgs(c.messages.slice(-10).reverse())).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <Section id="pin" icon={Pin} title="Pin">
      {e.pin && (
        <div className="mb-2 flex items-center gap-2 rounded-xl bg-x-gold-soft p-2" data-testid="hall-pinned">
          <Face p={e.pin} size={28} ring={ACC.gold} />
          <p className="line-clamp-2 min-w-0 flex-1 break-words text-xs"><b>@{e.pin.h}</b> {e.pin.text}</p>
          <Btn tone="soft" className="px-2" data-testid="unpin-button" aria-label="Unpin" disabled={busy} onClick={() => onPin(null)}><PinOff className="h-4 w-4" /></Btn>
        </div>
      )}
      <div className="hud-scroll max-h-60 overflow-y-auto">
        <List>
          {msgs.map((m, i) => (
            <Row key={m.id} i={i}>
              <div className="flex items-center gap-2">
                <Face p={m} size={26} ring={ACC.grey} />
                <p className="line-clamp-2 min-w-0 flex-1 break-words text-xs"><b>@{m.h}</b> {m.text}</p>
                <Btn tone="gold" className="px-2" data-testid="pin-button" aria-label="Pin" disabled={busy || e.pin?.id === m.id} onClick={() => onPin(m.id)}><Pin className="h-4 w-4" /></Btn>
              </div>
            </Row>
          ))}
        </List>
      </div>
    </Section>
  );
}

function Past({ past, onVisit }) {
  return (
    <Section id="past" icon={Landmark} title="Past">
      <div className="hud-scroll -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {past.map((p, i) => (
          <motion.div key={`${p.week}-${p.k}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.25 } }}
            className="flex w-24 shrink-0 flex-col items-center gap-1 rounded-xl bg-x-raised p-2 transition-colors hover:bg-x-active">
            <span className="relative">
              <Face p={p} size={40} ring={ACC.grey} />
              <Crown className="absolute -right-1.5 -top-2.5 h-4 w-4 rotate-12 text-x-gold" />
            </span>
            <Name p={p} onVisit={onVisit} className="w-full text-xs" />
            <span className="flex items-center gap-1.5 text-[11px] text-x-mute">
              {new Date(p.week).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}
              <span className="flex items-center gap-0.5 font-semibold text-x-ink"><Vote className="h-3 w-3 text-x-gold" />{p.ballots}</span>
            </span>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

export function HallPanel({ econ, me, onChanged, onVisit }) {
  const [e, setE] = useState(null), [busy, setBusy] = useState(false), [err, setErr] = useState(null);
  const load = useCallback(() => api("/election").then(setE).catch((x) => setErr(x.message)), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const post = async (path, body) => {
    setBusy(true);
    setErr(null);
    try {
      setE(await api(path, { method: "POST", body }));
      onChanged();
      return true;
    } catch (x) {
      setErr(x.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="hall-panel">
      {err && (
        <motion.div key={err} role="alert" data-testid="hall-error" animate={{ x: [0, -5, 5, -3, 3, 0] }} transition={{ duration: 0.35 }}
          className="mx-4 mt-3 rounded-xl bg-x-neg-soft px-3 py-2 text-sm font-medium text-x-neg-ink">{err}</motion.div>
      )}
      {e ? (
        <>
          <Section id="mayor" icon={Crown} title="Mayor" right={<span data-testid="hall-rules"><Info text={rules(e.fee)} /></span>}>
            <Stage e={e} onVisit={onVisit} />
          </Section>
          {e.is_mayor && <Decree e={e} busy={busy} onDecree={(kind) => post("/mayor/decree", { kind })} />}
          {e.is_mayor && <Address e={e} busy={busy} onSend={(text) => post("/mayor/address", { text })} />}
          {e.is_mayor && <Grant e={e} busy={busy} onGrant={(h, amount) => post("/mayor/grant", { h, amount })} />}
          {e.is_mayor && <PinCard e={e} busy={busy} onPin={(id) => post("/mayor/pin", { id })} />}
          <Race e={e} me={me} busy={busy} onVote={(h) => post("/election/ballot", { h })} onVisit={onVisit} />
          <Run e={e} uxd={econ?.uxd ?? 0} busy={busy} onRun={(slogan) => post("/election/run", { slogan })} />
          {e.past.length > 0 && <Past past={e.past} onVisit={onVisit} />}
        </>
      ) : !err && <div className="space-y-3 p-4"><Skel className="h-40" /><Skel className="h-20" /><Skel className="h-24" /></div>}
    </div>
  );
}
